import React from 'react';
import {
  getHeroContent, HeroImage, StoreLogo, SearchBar, CartBtn, TabContent, StoreFooter, FaHeart, InlineEditableText
} from './ThemeLayoutHelpers';

export default function MintLayout({ p }) {
  const T = p.storeTheme || {};
  const text = p.storefrontText || {};
  const copy = getHeroContent(p, {
    eyebrow: "Fresh from nature",
    title: "Pure. Simple. Sustainable.",
    subtitle: "Eco-friendly products for conscious living",
    primaryCta: "Shop Now",
    secondaryCta: "Our Story",
  });
  
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50" style={{ fontFamily: T.fontFamily || "Poppins, sans-serif" }}>
      {/* Decorative elements */}
      <div className="fixed top-20 left-10 w-64 h-64 bg-emerald-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="fixed bottom-20 right-10 w-96 h-96 bg-teal-200/20 rounded-full blur-3xl pointer-events-none" />

      {/* Navigation */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <StoreLogo storeInfo={p.storeInfo} border="border-2 border-emerald-300" />
          <div className="flex-1">
            <p className="font-bold text-emerald-900">{p.storeInfo?.businessName}</p>
            <p className="text-xs text-emerald-600">{p.storeInfo?.businessCategory}</p>
          </div>
          <SearchBar searchQuery={p.searchQuery} setSearchQuery={p.setSearchQuery} theme={T} className="flex-1 max-w-md" />
          <div className="flex items-center gap-2">
            <button onClick={p.handleGoToWishlist} className="p-2 rounded-full hover:bg-emerald-50 transition relative">
              <FaHeart className="text-emerald-600" />
              {p.wishlistCount > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center">{p.wishlistCount}</span>}
            </button>
            <CartBtn p={p} />
            {p.userMenuElement}
          </div>
        </div>
      </nav>

      {/* Hero with Stats */}
      <section className="max-w-7xl mx-auto w-full px-4 pt-8">
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div>
              <span className="inline-block px-3 py-1 rounded-full bg-emerald-100 text-emerald-700 text-xs font-semibold tracking-wide mb-4"><InlineEditableText p={p} field="heroEyebrow" value={copy.eyebrow}>{copy.eyebrow}</InlineEditableText></span>
              <h1 className="text-5xl font-bold text-emerald-900 leading-tight"><InlineEditableText p={p} field="heroTitle" value={copy.title}>{copy.title}</InlineEditableText></h1>
              <p className="mt-4 text-gray-600 text-lg"><InlineEditableText p={p} field="heroSubtitle" value={copy.subtitle}>{copy.subtitle}</InlineEditableText></p>
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[
                { label: "Happy Customers", value: "10k+", icon: "😊" },
                { label: "Products", value: p.filteredProducts?.length || 0, icon: "🌿" },
                { label: "5-Star Reviews", value: "98%", icon: "⭐" },
              ].map(stat => (
                <div key={stat.label} className="bg-white rounded-xl p-4 text-center shadow-sm border border-emerald-100">
                  <div className="text-2xl mb-1">{stat.icon}</div>
                  <div className="text-xl font-bold text-emerald-700">{stat.value}</div>
                  <div className="text-xs text-gray-500">{stat.label}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <button onClick={() => p.setActiveTab("products")} className="px-6 py-3 rounded-full bg-emerald-600 text-white font-semibold hover:bg-emerald-700 transition shadow-md"><InlineEditableText p={p} field="primaryCtaText" value={copy.primaryCta}>{copy.primaryCta}</InlineEditableText></button>
              <button onClick={() => p.setActiveTab("details")} className="px-6 py-3 rounded-full border border-emerald-300 text-emerald-700 font-semibold hover:bg-emerald-50 transition"><InlineEditableText p={p} field="secondaryCtaText" value={copy.secondaryCta}>{copy.secondaryCta}</InlineEditableText></button>
            </div>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl">
            <HeroImage p={p} themeId="mint" className="h-96">
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/40 to-transparent pointer-events-none" />
            </HeroImage>
          </div>
        </div>
      </section>

      {/* Categories Preview */}
      <section className="max-w-7xl mx-auto w-full px-4 mt-12">
        <div className="flex gap-3 overflow-x-auto pb-4">
          {[text.products || "Products", text.services || "Services", "Sustainability", "Community"].map((cat, idx) => (
            <button key={idx} onClick={() => p.setActiveTab(idx === 0 ? "products" : idx === 1 ? "services" : "details")} className="px-6 py-2 rounded-full bg-white border border-emerald-200 text-emerald-700 text-sm font-semibold whitespace-nowrap hover:bg-emerald-50 transition">
              {cat}
            </button>
          ))}
        </div>
      </section>

      <section className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        <TabContent p={p} />
      </section>

      <StoreFooter p={p} />
    </div>
  );
}